import { PDFDocument } from "pdf-lib";
import { applyDashStyle } from "../canvas/dashStyle";
import { MM_PER_IN, PT_PER_MM, clamp } from "./units";
import type { Layout } from "./types";
import type { AppState } from "../state/store";

export interface ExportProgress {
  (msgKey: "busy.pdfBuild" | "busy.pdfPage" | "busy.saving",
   vars?: { n?: number; total?: number }): void;
}

export interface ExportArgs {
  state: Pick<AppState, "source" | "margins" | "sign" | "cut" | "output">;
  layout: Layout;
  onProgress: ExportProgress;
}

const tick = (): Promise<void> => new Promise((res) => setTimeout(res, 0));

export const exportTiledPdf = async ({
  state, layout: L, onProgress,
}: ExportArgs): Promise<{ blob: Blob; filename: string }> => {
  const { source, margins, sign, cut, output } = state;
  if (!source) throw new Error("No source loaded");

  onProgress("busy.pdfBuild");

  const pdf = await PDFDocument.create();
  const pxPerMm = output.dpi / MM_PER_IN;

  // For PDF source: re-render the chosen page at high resolution.
  let hrSource: CanvasImageSource = source.image;
  let hrW = source.widthPx;
  let hrH = source.heightPx;
  if (source.type === "pdf") {
    const page = await source.pdfDoc.getPage(source.pageNum);
    const targetWidthPx = sign.width * pxPerMm;
    const baseVp = page.getViewport({ scale: 1 });
    const scale = clamp(targetWidthPx / baseVp.width, 1, 8);
    const vp = page.getViewport({ scale });
    const c = document.createElement("canvas");
    c.width  = Math.ceil(vp.width);
    c.height = Math.ceil(vp.height);
    const ctx = c.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    hrSource = c;
    hrW = c.width;
    hrH = c.height;
  }

  const totalPages = L.cols * L.rows;
  for (let r = 0; r < L.rows; r++) {
    for (let c = 0; c < L.cols; c++) {
      const idx = r * L.cols + c + 1;
      onProgress("busy.pdfPage", { n: idx, total: totalPages });
      await tick();

      const pageW_pt = L.paper.width  * PT_PER_MM;
      const pageH_pt = L.paper.height * PT_PER_MM;
      const canvasW = Math.round(L.paper.width  * pxPerMm);
      const canvasH = Math.round(L.paper.height * pxPerMm);

      const pc = document.createElement("canvas");
      pc.width = canvasW;
      pc.height = canvasH;
      const pctx = pc.getContext("2d")!;
      pctx.fillStyle = "#ffffff";
      pctx.fillRect(0, 0, canvasW, canvasH);

      const sxMm = c * L.availW;
      const syMm = r * L.availH;
      const swMm = Math.min(L.availW, sign.width  - sxMm);
      const shMm = Math.min(L.availH, sign.height - syMm);
      if (swMm > 0 && shMm > 0) {
        pctx.imageSmoothingEnabled = true;
        pctx.imageSmoothingQuality = "high";
        pctx.drawImage(
          hrSource,
          (sxMm / sign.width)  * hrW,
          (syMm / sign.height) * hrH,
          (swMm / sign.width)  * hrW,
          (shMm / sign.height) * hrH,
          margins.left * pxPerMm,
          margins.top  * pxPerMm,
          swMm * pxPerMm,
          shMm * pxPerMm,
        );
      }

      if (cut.enabled) {
        const tk = Math.max(1, cut.thicknessMm * pxPerMm);
        pctx.save();
        pctx.strokeStyle = "#000";
        pctx.lineWidth = tk;
        applyDashStyle(pctx, cut.style, tk);
        pctx.strokeRect(
          margins.left * pxPerMm,
          margins.top  * pxPerMm,
          L.availW * pxPerMm,
          L.availH * pxPerMm,
        );
        pctx.restore();
      }

      const dataUrl = pc.toDataURL("image/png");
      const png = await pdf.embedPng(dataUrl);
      const newPage = pdf.addPage([pageW_pt, pageH_pt]);
      newPage.drawImage(png, { x: 0, y: 0, width: pageW_pt, height: pageH_pt });
    }
  }

  onProgress("busy.saving");
  const bytes = await pdf.save();
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const baseName = source.name.replace(/\.[^.]+$/, "");
  const filename = `${baseName}_tiled_${L.cols}x${L.rows}.pdf`;
  return { blob, filename };
};
